/**
 * Pricing Section - Landing Page
 */

import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { Card, Button } from '../../../../design-system';
import { ROUTES } from '../../../../config/routes';
import { useNavigate } from 'react-router-dom';
import { plansAPI } from '../../../../utils/api';

const Pricing = ({ language }) => {
  const isArabic = language === 'ar';
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await plansAPI.getPricing();
        if (response.data.success && response.data.data) {
          // Process plans from backend
          const processedPlans = response.data.data.map((plan, index) => {
            let features = [];
            if (plan.features) {
              if (typeof plan.features === 'string') {
                try {
                  features = JSON.parse(plan.features);
                } catch (e) {
                  features = [];
                }
              } else if (Array.isArray(plan.features)) {
                features = plan.features;
              }
            }
            
            return {
              id: plan.id,
              name: plan.name,
              price: plan.price_usd || 'Custom',
              description: plan.description || '',
              features: features,
              otpLimit: plan.otp_limit_monthly,
              apiKeys: plan.api_keys_limit,
              supportLevel: plan.support_level,
              popular: index === 1 || plan.popular === 1, // Second plan is popular, or if marked as popular
            };
          });
          setPlans(processedPlans);
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError('Failed to load plans');
        // Fallback to empty state
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  return (
    <section id="pricing" className="w-full py-16 sm:py-24 md:py-32 lg:py-48 px-3 sm:px-4 bg-[#0B0E14] relative">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#128C7E]/5 via-transparent to-transparent pointer-events-none" />
      
      <div className="relative max-w-6xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center mb-4 sm:mb-6">
            {isArabic ? 'الأسعار' : 'Pricing'}
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-[#A0AEC0] max-w-2xl mx-auto">
            {isArabic ? 'خطط مرنة مصممة لجميع أحجام الأعمال' : 'Flexible plans designed for businesses of all sizes'}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#128C7E]"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-[#A0AEC0] text-base">
              {isArabic ? 'حدث خطأ في تحميل الخطط' : 'Failed to load plans'}
            </p>
          </div>
        )}

        {/* Plans Grid */}
        {!loading && !error && plans.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-6 auto-rows-max">
            {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative ${plan.popular ? 'sm:col-span-2 lg:col-span-1 lg:row-span-1' : ''}`}
            >
              {/* Glowing border for popular plan */}
              {plan.popular && (
                <>
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#128C7E] via-[#128C7E]/50 to-[#128C7E] rounded-2xl opacity-75 blur-lg" />
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-[#128C7E] to-[#128C7E]/30 rounded-2xl" />
                </>
              )}
              
              <Card
                glass={!plan.popular}
                className={`relative p-6 sm:p-8 flex flex-col h-full ${
                  plan.popular
                    ? 'bg-[#1A1D23] border-2 border-[#128C7E]/80'
                    : 'hover:border-[#128C7E]/30'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 sm:-top-5 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-[#128C7E] to-[#0D6B62] text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold shadow-lg shadow-[#128C7E]/20">
                      {isArabic ? '⭐ الأكثر شيوعاً' : '⭐ Most Popular'}
                    </span>
                  </div>
                )}

                <div className={plan.popular ? 'mt-4 sm:mt-6' : ''}>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-[#A0AEC0] text-xs sm:text-sm mb-6 min-h-10">
                    {plan.description}
                  </p>
                  <div className="mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-[#2D3748]">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl sm:text-5xl font-bold text-white">
                        {typeof plan.price === 'string' && plan.price !== 'Custom'
                          ? `$${plan.price}`
                          : plan.price}
                      </span>
                      {plan.price !== 'Custom' && (
                        <span className="text-[#A0AEC0] text-xs sm:text-sm">
                          {isArabic ? '/شهري' : '/month'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Plan Details */}
                  <div className="mb-6 sm:mb-8 p-4 bg-[#0B0E14]/50 rounded-lg space-y-3 border border-[#2D3748]">
                    <div className="flex items-center justify-between">
                      <span className="text-[#A0AEC0] text-xs sm:text-sm">{isArabic ? 'حد OTP الشهري' : 'OTP Limit/Month'}:</span>
                      <span className="font-semibold text-white text-sm sm:text-base">{plan.otpLimit?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#A0AEC0] text-xs sm:text-sm">{isArabic ? 'مفاتيح API' : 'API Keys'}:</span>
                      <span className="font-semibold text-white text-sm sm:text-base">{plan.apiKeys}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#A0AEC0] text-xs sm:text-sm">{isArabic ? 'مستوى الدعم' : 'Support Level'}:</span>
                      <span className="px-2 sm:px-3 py-1 bg-[#128C7E]/20 text-[#128C7E] rounded-full text-xs font-medium capitalize">
                        {isArabic 
                          ? (plan.supportLevel === 'email' ? 'دعم البريد الإلكتروني' : plan.supportLevel === 'priority' ? 'دعم ذو أولوية' : 'دعم متميز')
                          : (plan.supportLevel === 'email' ? 'Email Support' : plan.supportLevel === 'priority' ? 'Priority Support' : 'Premium Support')
                        }
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex-1 mb-6 sm:mb-8">
                  <ul className="space-y-3 sm:space-y-4">
                    {Array.isArray(plan.features) && plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-3">
                        <Check className="w-4 h-4 sm:w-5 sm:h-5 text-[#128C7E] flex-shrink-0 mt-1 sm:mt-0.5" />
                        <span className="text-[#A0AEC0] text-xs sm:text-sm leading-relaxed">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button
                  variant={plan.popular ? 'primary' : 'secondary'}
                  size="lg"
                  className="w-full text-sm sm:text-base"
                  onClick={() => navigate(ROUTES.LOGIN)}
                >
                  {isArabic ? 'ابدأ الآن مجاناً' : 'Get Started Free'}
                </Button>
              </Card>
            </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && plans.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[#A0AEC0] text-base">
              {isArabic ? 'لا توجد خطط متاحة حالياً' : 'No plans available at the moment'}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Pricing;
